/* eslint-disable react-refresh/only-export-components -- co-locacion intencional de componente + helpers/hooks muy usados; la regla es solo de Fast Refresh (dev), sin impacto en produccion */
/**
 * Provider de autenticación LOCAL para desarrollo / piloto Dexie.
 *
 * Cambios v2 (auditoría de auth):
 *
 *   1. **Lista de usuarios dinámica**: ya no hay `SAMPLE_USERS` hardcoded.
 *      Los usuarios disponibles para login se leen del módulo Autoridades
 *      (Dexie tabla `usuarios`, tenant "default"). Si la tabla no tiene
 *      data (primera ejecución), se aplica el seed automático.
 *
 *   2. **Login con clave**: cada usuario tiene una clave local persistida
 *      en localStorage (hash SHA-256 + salt por usuario). El ADMIN_PMIS
 *      tiene acceso libre sin clave (decisión del producto — el ADMIN
 *      gobierna el sistema y necesita acceso rápido para soporte).
 *      Los demás usuarios deben digitar su clave.
 *
 *   3. **Establecer clave**: si un usuario no tiene clave aún (estado
 *      inicial del piloto), al primer login se le pide elegir una y queda
 *      guardada. Después, los siguientes login requieren esa clave.
 *
 *   4. **Reset de clave**: el ADMIN_PMIS puede resetear la clave de
 *      cualquier usuario desde la pantalla de Autoridades (TODO en otra
 *      iteración).
 *
 * En SharePoint (cuando se active MSAL), este provider no se usa — el
 * usuario es el dueño de la sesión SP del navegador y no hay password
 * adicional. Es el camino que aplicará en producción.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Role } from "@constants/roles";
import { seedIfEmpty } from "@/core/data/adapters/dexie/seed";
import { buildRepos } from "@/core/data/repos";

// Backend activo. En "supabase" el padrón de usuarios se lee del repo
// (Supabase, compartido); en "dexie" se siembra y se lee localmente.
const AUTH_BACKEND = import.meta.env.VITE_BACKEND ?? "sharepoint";
import {
  CLAVE_DEFAULT_PILOTO,
  CLAVE_MAESTRA_PRUEBAS,
  HARDEN_AUTH,
  establecerClave,
  hydrateCredenciales,
  leerModoAcceso,
  tieneClave,
  verificarClave,
  verificarClaveAdmin,
} from "./credenciales";

// ════════════════════════════════════════════════════════════════════
// Tenant de los datos locales
// ════════════════════════════════════════════════════════════════════
// IMPORTANTE: este string DEBE coincidir con el `TENANT_LOCAL` del seed
// (`src/core/data/adapters/dexie/seed.ts`). Si se desincronizan, el
// provider no encuentra los usuarios y la pantalla de login queda vacía.
const TENANT_LOCAL = "local-dev";

const STORAGE_USER = "pmis_local_user";
/** Identidad del ADMIN real mientras "ve como" otro usuario (impersonación). */
const STORAGE_ADMIN_REAL = "pmis_local_admin_real";

export interface LocalUser {
  email: string;
  name: string;
  /** Rol con el que está actualmente autenticado (puede ser primario o secundario). */
  role: Role;
  /** Rol primario del registro Usuario. */
  rolPrimario: Role;
  /** Roles a los que este email tiene derecho (primario + secundarios). */
  rolesDisponibles: Role[];
}

export interface LoginOptions {
  /**
   * Rol con el que el usuario quiere ingresar. Debe ser uno de los
   * `rolesDisponibles` del usuario. Si se omite, se usa el rol primario.
   */
  targetRole?: Role;
  /**
   * Segunda clave, requerida obligatoriamente cuando `targetRole === "ADMIN_PMIS"`.
   * Es la clave de administración configurada en `credenciales.ts` (cambiable
   * por el propio admin). Ningún otro `targetRole` la requiere.
   */
  adminPassword?: string;
}

interface LocalAuthContextValue {
  user: LocalUser | null;
  /** Identidad del ADMIN real cuando está impersonando a otro usuario; null si no. */
  adminReal: LocalUser | null;
  tenantId: string;
  isAuthenticated: boolean;
  /**
   * Intenta autenticar al usuario.
   *
   * Modelo de dos pasos para ADMIN_PMIS:
   *   1. Valida `password` contra la base (clave maestra de pruebas, clave
   *      personal del usuario, o fallback de rescate según `ModoAcceso`).
   *   2. Si `targetRole === "ADMIN_PMIS"`, valida `adminPassword` contra la
   *      clave de admin configurada. Ambas deben pasar.
   *
   * Para cualquier otro `targetRole` solo se requiere la clave base.
   */
  login: (
    email: string,
    password: string,
    options?: LoginOptions,
  ) => Promise<{ ok: boolean; mensaje?: string; primeraVez?: boolean }>;
  /** Cierra sesión sin afectar credenciales. */
  logout: () => void;
  /** Lista de usuarios activos del PMIS (lectura del módulo Autoridades). */
  availableUsers: LocalUser[];
  /** El padrón aún se está cargando: el login debe esperar, no rechazar. */
  padronCargando: boolean;
  /** Reinicio o creación de clave (solo ADMIN_PMIS puede llamarlo en UI). */
  setPassword: (email: string, newPassword: string) => Promise<void>;
  /** ¿El usuario ya tiene clave registrada? */
  tienePassword: (email: string) => boolean;
  /**
   * Impersona a otro usuario sin requerir su clave. Solo el ADMIN_PMIS
   * debe poder llamarlo (la UI ya restringe el switcher; el guard de
   * autorización vive ahí). El switcher lo usa para "actuar como" durante
   * soporte/testing. Las acciones realizadas quedan auditadas con el
   * email del usuario impersonado.
   */
  loginAs: (email: string) => void;
}

const LocalAuthContext = createContext<LocalAuthContextValue | null>(null);

// ════════════════════════════════════════════════════════════════════
// Provider
// ════════════════════════════════════════════════════════════════════

interface LocalAuthProviderProps {
  children: ReactNode;
}

export function LocalAuthProvider({ children }: LocalAuthProviderProps) {
  const [user, setUser] = useState<LocalUser | null>(() => {
    const stored = sessionStorage.getItem(STORAGE_USER);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as LocalUser;
    } catch {
      return null;
    }
  });
  const [availableUsers, setAvailableUsers] = useState<LocalUser[]>([]);
  /**
   * El padrón y el mapa de credenciales se cargan por red. Mientras no lleguen,
   * `availableUsers` está vacío y CUALQUIER intento de ingreso —con la clave
   * correcta— falla con "Usuario no encontrado o inactivo". El formulario debe
   * poder esperar en vez de rechazar al usuario.
   */
  const [padronCargando, setPadronCargando] = useState(true);
  // Identidad del ADMIN real cuando está impersonando ("viendo como") a otro
  // usuario. Mientras esté seteada, el switcher sigue visible y permite VOLVER
  // a Administrador sin cerrar sesión.
  const [adminReal, setAdminReal] = useState<LocalUser | null>(() => {
    const stored = sessionStorage.getItem(STORAGE_ADMIN_REAL);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as LocalUser;
    } catch {
      return null;
    }
  });

  // Carga usuarios reales del módulo Autoridades (Dexie). PRIMERO espera al
  // seed inicial para evitar la condición de carrera donde la consulta corría
  // antes que `seedIfEmpty()` poblara la tabla y la lista quedaba vacía.
  useEffect(() => {
    void (async () => {
      // En dexie (demo local) se siembra el padrón local. En supabase el
      // padrón vive compartido en la nube; no se siembra Dexie para el login.
      if (AUTH_BACKEND === "dexie") {
        try {
          await seedIfEmpty();
        } catch (e) {
          console.warn("[Auth] seedIfEmpty falló, continuamos:", e);
        }
      }
      // Hidrata el mapa de credenciales (claves cifradas) desde el backend —
      // en supabase es compartido, así las claves fijadas/restablecidas por el
      // admin se ven en todos los equipos.
      await hydrateCredenciales();
      // El padrón se lee SIEMPRE por el repo: en supabase apunta a la nube
      // (compartido entre todos los equipos), en dexie a la base local. Esto
      // garantiza que el login y el módulo Autoridades vean el mismo padrón.
      const rows = await buildRepos(TENANT_LOCAL).usuario.listActive();
      const activos = rows
        .map<LocalUser>((u) => {
          const rolesDisponibles = [
            u.rol,
            ...(u.rolesSecundarios ?? []),
          ];
          return {
            email: u.email,
            name: u.nombre,
            role: u.rol,
            rolPrimario: u.rol,
            rolesDisponibles,
          };
        })
        .sort((a, b) => {
          // ADMIN_PMIS primero (sea primario o secundario), luego por nombre.
          const aIsAdmin = a.rolesDisponibles.includes("ADMIN_PMIS");
          const bIsAdmin = b.rolesDisponibles.includes("ADMIN_PMIS");
          if (aIsAdmin && !bIsAdmin) return -1;
          if (bIsAdmin && !aIsAdmin) return 1;
          return a.name.localeCompare(b.name);
        });
      setAvailableUsers(activos);
    })()
      .catch(() => {
        // Sin padrón no se puede autenticar; el formulario lo informa.
      })
      .finally(() => setPadronCargando(false));
  }, []);

  useEffect(() => {
    if (user) {
      sessionStorage.setItem(STORAGE_USER, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(STORAGE_USER);
    }
  }, [user]);

  useEffect(() => {
    if (adminReal) {
      sessionStorage.setItem(STORAGE_ADMIN_REAL, JSON.stringify(adminReal));
    } else {
      sessionStorage.removeItem(STORAGE_ADMIN_REAL);
    }
  }, [adminReal]);

  // ── API expuesta ────────────────────────────────────────────────────

  const login = async (
    email: string,
    password: string,
    options?: LoginOptions,
  ): Promise<{ ok: boolean; mensaje?: string; primeraVez?: boolean }> => {
    const u = availableUsers.find(
      (x) => x.email.toLowerCase() === email.toLowerCase(),
    );
    const modo = leerModoAcceso();

    // ── Validación interna de la clave BASE según modo ───────────────
    const validarBase = async (user: LocalUser): Promise<boolean> => {
      // Modo PRUEBAS: la clave maestra siempre vale como base, además de
      // la clave personal que el usuario pudiera tener asignada.
      if (!HARDEN_AUTH && modo === "clave-maestra" && password === CLAVE_MAESTRA_PRUEBAS) {
        return true;
      }
      const v = await verificarClave(user.email, password);
      return v !== "nope";
    };

    // ── Modo rescate (DB rota, email canónico del admin) ─────────────
    if (!u) {
      if (!HARDEN_AUTH && email.trim().toLowerCase() === "admin.pmis@tecpetrol.com") {
        if (password !== CLAVE_DEFAULT_PILOTO) {
          return { ok: false, mensaje: "Credenciales incorrectas." };
        }
        // Aun en rescate, si se pidió target ADMIN_PMIS exigimos la 2da clave.
        if (options?.targetRole === "ADMIN_PMIS") {
          const okAdmin = await verificarClaveAdmin(options.adminPassword ?? "");
          if (!okAdmin) return { ok: false, mensaje: "Credenciales incorrectas." };
        }
        const rescue: LocalUser = {
          email: "admin.pmis@tecpetrol.com",
          name: "Administrador PMIS (rescate)",
          role: "ADMIN_PMIS",
          rolPrimario: "ADMIN_PMIS",
          rolesDisponibles: ["ADMIN_PMIS"],
        };
        setUser(rescue);
        return { ok: true };
      }
      return { ok: false, mensaje: "Usuario no encontrado o inactivo." };
    }

    // ── Determinar el rol objetivo ────────────────────────────────────
    const targetRole = options?.targetRole ?? u.rolPrimario;
    if (!u.rolesDisponibles.includes(targetRole)) {
      return {
        ok: false,
        mensaje: "Este usuario no tiene autorización para ingresar con ese rol.",
      };
    }

    // ── Paso 1: validar clave BASE ────────────────────────────────────
    const baseOk = await validarBase(u);
    if (!baseOk) {
      return {
        ok: false,
        mensaje: "Credenciales incorrectas.",
      };
    }

    // ── Paso 2: si entra como ADMIN_PMIS, exigir 2da clave ────────────
    if (targetRole === "ADMIN_PMIS") {
      const okAdmin = await verificarClaveAdmin(options?.adminPassword ?? "");
      if (!okAdmin) {
        return {
          ok: false,
          mensaje: "Credenciales incorrectas.",
        };
      }
    }

    // ── Autenticado: fijar usuario con el rol elegido ─────────────────
    const sesion: LocalUser = {
      email: u.email,
      name: u.name,
      role: targetRole,
      rolPrimario: u.rolPrimario,
      rolesDisponibles: u.rolesDisponibles,
    };
    setUser(sesion);
    return { ok: true };
  };

  const setPassword = async (email: string, newPassword: string): Promise<void> => {
    await establecerClave(email, newPassword);
  };

  const tienePassword = (email: string): boolean => tieneClave(email);

  const loginAs = (email: string): void => {
    // VOLVER a Administrador: restaura la identidad admin real exacta (con su
    // rol ADMIN_PMIS) y sale del modo impersonación.
    if (adminReal && email.toLowerCase() === adminReal.email.toLowerCase()) {
      setUser(adminReal);
      setAdminReal(null);
      return;
    }
    const u = availableUsers.find(
      (x) => x.email.toLowerCase() === email.toLowerCase(),
    );
    if (!u) return;
    // Si un ADMIN empieza a "ver como" otro usuario, recordamos su identidad
    // real para poder volver sin cerrar sesión.
    if (user && user.role === "ADMIN_PMIS") {
      setAdminReal(user);
    }
    // Impersonación: usa el rol primario del usuario destino.
    setUser({
      email: u.email,
      name: u.name,
      role: u.rolPrimario,
      rolPrimario: u.rolPrimario,
      rolesDisponibles: u.rolesDisponibles,
    });
  };

  const value: LocalAuthContextValue = {
    user,
    adminReal,
    tenantId: TENANT_LOCAL,
    isAuthenticated: user !== null,
    login,
    logout: () => {
      setUser(null);
      setAdminReal(null);
    },
    availableUsers,
    padronCargando,
    setPassword,
    tienePassword,
    loginAs,
  };

  return <LocalAuthContext.Provider value={value}>{children}</LocalAuthContext.Provider>;
}

export function useLocalAuth(): LocalAuthContextValue {
  const ctx = useContext(LocalAuthContext);
  if (!ctx) throw new Error("useLocalAuth must be used within LocalAuthProvider");
  return ctx;
}
